import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Smartphone } from "lucide-react";
import BlurText from "@/components/ui/BlurText";
import RotatingSport from "./RotatingSport";
import { lazy, Suspense } from "react";
const HeroLineupDemo = lazy(() => import("./HeroLineupDemo"));

// Hero copy lives here so we can iterate / A/B without hunting through JSX.
// Change values and ship, no other code edits needed.
const HERO_VARIANT = {
  badge: "LineupSync · Free, no player limit, no card",
  subhead: "",
  headlineLeadBefore: "A free",
  headlineLeadAfter: "team management app",
  headlineAccent: "that chases the RSVPs for you.",
  subtitle:
    "Put the fixture in once. LineupSync asks every player, reminds the ones who go quiet, and then builds your lineup from whoever actually said yes. Made for one coach running the whole thing on a phone.",
  primaryCta: "Start your free team",
  primaryMicrocopy: "About a minute to set up. No credit card.",
  secondaryCta: "See how it works",
  trustLine: "LineupSync is used by Sunday league, rec and small club coaches in the UK, Germany and Estonia",
  bullets: [
    { icon: Zap, label: "Players answer from the email, no account" },
    { icon: ShieldCheck, label: "Unlimited players and staff, free" },
    { icon: Smartphone, label: "Built for a phone at the side of a pitch" },
  ],
};

const HeroSection = () => {
  return (
    <section className="relative flex items-center justify-center pt-16 overflow-hidden">
      {/* Subtle static backdrop, no animation, no shader. Sits behind hero only. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 60% 50% at 15% 20%, hsl(var(--primary) / 0.12), transparent 70%), radial-gradient(ellipse 50% 40% at 85% 90%, hsl(var(--accent) / 0.08), transparent 70%)",
        }}
      />
      <div className="container relative mx-auto px-4 py-20 md:py-32">
        <div className="max-w-6xl mx-auto grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:items-center lg:text-left text-center">
          <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
          >
            {HERO_VARIANT.badge}
          </motion.div>

          {HERO_VARIANT.subhead && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-6"
            >
              {HERO_VARIANT.subhead}
            </motion.p>
          )}

          <h1
            aria-label={`${HERO_VARIANT.headlineLeadBefore} football ${HERO_VARIANT.headlineLeadAfter} ${HERO_VARIANT.headlineAccent}`}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] mb-6 flex flex-col items-center lg:items-start gap-2"
          >
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="block"
            >
              {HERO_VARIANT.headlineLeadBefore} <RotatingSport /> {HERO_VARIANT.headlineLeadAfter}
            </motion.span>
            <BlurText
              as="span"
              text={HERO_VARIANT.headlineAccent}
              delay={120}
              animateBy="words"
              direction="top"
              stepDuration={0.4}
              className="justify-center lg:justify-start"
              gradient="linear-gradient(to right, hsl(var(--primary)), hsl(var(--accent)))"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-10"
          >
            {HERO_VARIANT.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex justify-center lg:justify-start"
          >
            <div className="flex flex-col items-center lg:items-start gap-3">
              <Button variant="hero" size="xl" asChild>
                <Link to="/auth?tab=signup">
                  {HERO_VARIANT.primaryCta} <ArrowRight className="ml-1" size={20} />
                </Link>
              </Button>
              <p className="text-xs text-muted-foreground">{HERO_VARIANT.primaryMicrocopy}</p>
              <a
                href="#features"
                className="mt-1 text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
              >
                {HERO_VARIANT.secondaryCta} ↓
              </a>
            </div>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-14 flex flex-col items-center lg:items-start gap-5"
          >
            <p className="text-xs uppercase tracking-wider text-muted-foreground/80">
              {HERO_VARIANT.trustLine}
            </p>
            <ul className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-sm text-muted-foreground">
              {HERO_VARIANT.bullets.map(({ icon: Icon, label }) => (
                <li key={label} className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary">
                    <Icon size={14} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </motion.div>
          </div>

          {/* Product demo, animated lineup builder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="order-last lg:order-none"
          >
            <Suspense fallback={<div className="aspect-[4/3] rounded-2xl bg-card/40 border border-border/40" />}>
              <HeroLineupDemo />
            </Suspense>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
